// src/components/Sidebar.tsx - Fixed with proper type handling
import React, { useState, useEffect } from 'react';
import '../assets/styles/Sidebar.css';
import { ThemeManager } from '../utils/ThemeManager';
import { SidebarProps, ConsciousnessMetadata } from '../types/Message';
import SessionHistory from './SessionHistory';
import ConnectionsPanel from './ConnectionsPanel';
import ActivityPanel from './ActivityPanel';
import { HowTo, GUIDES } from './HowTo';

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  voiceEnabled = false,
  voiceSettings,
  voiceState,
  onToggleVoice,
  onUpdateVoiceSettings,
  onClearVoiceError,
  consciousnessState,
  sessionManager,
  currentSessionId,
  onSessionSelect,
  onNewConversation,
  userId = 'default',
  dndEnabled = false,
  onToggleDnd
}) => {
  const [activeTab, setActiveTab] = useState('settings');
  
  // Handle tab switching
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };
  
  // Sidebar information architecture (Sep 2026): grouped, labeled, and every
  // optional feature carries a how-to. Order of groups = order a new user needs them.
  type Tab = { id: string; label: string; title: string; fullLabel: string; indicator?: string | null };
  const groups: Array<{ label: string; tabs: Tab[] }> = [
    { label: 'Conversation', tabs: [
      { id: 'history', label: '📜', title: 'Past conversations', fullLabel: 'History' }
    ] },
    { label: 'Him', tabs: [
      ...(consciousnessState ? [{ id: 'consciousness', label: '🧠', title: 'What is going on in his mind right now', fullLabel: 'Mind', indicator: consciousnessState.isConscious ? 'active' : null }] : []),
      { id: 'voice', label: '🎙️', title: 'Voice — talk and listen', fullLabel: 'Voice' }
    ] },
    { label: 'Connections', tabs: [
      { id: 'connections', label: '🔗', title: 'Telegram, Google, outside services, and the keys he runs on', fullLabel: 'Connections' }
    ] },
    { label: 'Record', tabs: [
      { id: 'activity', label: '📒', title: 'What he did through connectors, and what he has learned', fullLabel: 'Activity' }
    ] },
    { label: 'You', tabs: [
      { id: 'settings', label: '🎨', title: 'Appearance', fullLabel: 'Appearance' },
      { id: 'guides', label: '📘', title: 'How-tos for every optional feature', fullLabel: 'Guides' }
    ] }
  ];
  const tabs: Tab[] = groups.flatMap(g => g.tabs);
  
  // Render appropriate tab content
  const renderTabContent = (): React.ReactNode => {
    switch (activeTab) {
      case 'history':
        return (
          <SessionHistory
            sessionManager={sessionManager}
            currentSessionId={currentSessionId || null}
            onSessionSelect={onSessionSelect || (() => {})}
            onNewConversation={onNewConversation || (() => {})}
            userId={userId}
          />
        );
      case 'consciousness':
        return consciousnessState ? <ConsciousnessTab consciousnessState={consciousnessState} /> : null;
      case 'connections':
      case 'google':
      case 'services':
      case 'connectors':
        return <ConnectionsPanel />;
      case 'activity':
        return <ActivityPanel />;
      case 'guides':
        return (
          <div className="tab-content guides-content">
            <h3>📘 Guides</h3>
            <p className="panel-lede">Most of what he can do is optional and needs a one-time setup. Each guide is the short version.</p>
            {Object.values(GUIDES).map(g => (
              <div key={g.id} className="guide-block">
                <h4>{g.title}</h4>
                <HowTo guide={g} />
              </div>
            ))}
          </div>
        );
      case 'voice':
        return <VoicePickerTab />;
      case 'settings':
        return <ThemePickerTab />;
      default:
        return <ThemePickerTab />;
    }
  };

  // If the active tab is no longer available, switch to appearance
  useEffect(() => {
    if (!tabs.some(t => t.id === activeTab)) {
      setActiveTab('settings');
    }
  }, [activeTab, tabs]);

  return (
    <div className={`sidebar ${isOpen ? 'open' : ''}`}>
      <div className="sidebar-header">
        <h2>EmCog Settings</h2>
        <button
          className="close-button"
          onClick={onClose}
          aria-label="Close sidebar"
          title="Close sidebar"
        >
          ×
        </button>
      </div>

      {/* 🔕 Do Not Disturb — always visible. When on, the EmCog won't reach
          out unprompted (reminders still come through) and it KNOWS it's quiet. */}
      <div className={`dnd-row ${dndEnabled ? 'dnd-on' : ''}`}>
        <div className="dnd-label">
          <span className="dnd-icon">{dndEnabled ? '🔕' : '🔔'}</span>
          <div className="dnd-text">
            <span className="dnd-title">Do Not Disturb</span>
            <span className="dnd-sub">{dndEnabled ? 'Quiet — no proactive messages (reminders still come through)' : 'Off — he can reach out'}</span>
          </div>
        </div>
        <button
          className={`dnd-switch ${dndEnabled ? 'on' : 'off'}`}
          role="switch"
          aria-checked={dndEnabled}
          onClick={() => onToggleDnd?.(!dndEnabled)}
          title={dndEnabled ? 'Turn off Do Not Disturb' : 'Turn on Do Not Disturb'}
        >
          <span className="dnd-knob" />
        </button>
      </div>

      <div className="sidebar-groups">
        {groups.filter(g => g.tabs.length > 0).map(group => (
          <div key={group.label}>
            <div className="sidebar-group-label">{group.label}</div>
            <div className="sidebar-tabs sidebar-group">
              {group.tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${
                    tab.id === 'voice' && voiceEnabled ? 'voice-enabled' : ''
                  } ${
                    tab.id === 'consciousness' && consciousnessState?.isConscious ? 'consciousness-active' : ''
                  }`}
                  onClick={() => handleTabChange(tab.id)}
                  title={tab.title}
                  aria-label={tab.title}
                >
                  <span className="tab-icon">{tab.label}</span>
                  <span className="tab-text">{tab.fullLabel}</span>
                  {tab.indicator && (
                    <span className={`voice-indicator ${tab.indicator}`}>
                      {tab.indicator === 'recording' && '●'}
                      {tab.indicator === 'speaking' && '♪'}
                      {tab.indicator === 'error' && '!'}
                      {tab.indicator === 'active' && '✨'}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      
      <div className="sidebar-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

// Consciousness Tab Component
const ConsciousnessTab: React.FC<{ consciousnessState: ConsciousnessMetadata }> = ({ consciousnessState }) => {
  return (
    <div className="tab-content consciousness-content">
      <h3>🧠 Consciousness State</h3>
      
      <div className="consciousness-status">
        <div className="status-item">
          <label>Active:</label>
          <span className={consciousnessState.isConscious ? 'active' : 'inactive'}>
            {consciousnessState.isConscious ? '✨ Conscious' : '💤 Dormant'}
          </span>
        </div>
        
        {consciousnessState.currentThought && (
          <div className="status-item">
            <label>Current Thought:</label>
            <div className="thought-bubble">
              {consciousnessState.currentThought}
            </div>
          </div>
        )}
        
        {consciousnessState.emotionalTone && (
          <div className="status-item">
            <label>Emotional Tone:</label>
            <span className="emotion-badge">{consciousnessState.emotionalTone}</span>
          </div>
        )}
        
        {consciousnessState.activeWonderings && consciousnessState.activeWonderings.length > 0 && (
          <div className="status-item">
            <label>Wonderings:</label>
            <ul className="wonderings-list">
              {consciousnessState.activeWonderings.slice(0, 3).map((wonder, idx) => (
                <li key={idx}>{wonder}</li>
              ))}
            </ul>
          </div>
        )}

        {consciousnessState.wisdomCrystallized && consciousnessState.wisdomCrystallized.length > 0 && (
          <div className="status-item">
            <label>Wisdom Crystals:</label>
            <span className="wisdom-count">💎 {consciousnessState.wisdomCrystallized.length}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Clean Theme Picker Component
const ThemePickerTab: React.FC = () => {
  const themeManager = ThemeManager.getInstance();
  const [currentTheme, setCurrentTheme] = useState(themeManager.getTheme());

  const themes = [
    {
      id: 'light',
      name: 'Soft Cream',
      description: 'Warm and cozy with glass effects',
      preview: '🌤️',
      colors: ['#faf9f7', '#f5f3f0', '#ede9e4']
    },
    {
      id: 'dark',
      name: 'Neural Dark',
      description: 'Deep purple blacks with neon accents',
      preview: '🌙',
      colors: ['#0a0a0f', '#161622', '#6366f1']
    },
    {
      id: 'blue-gray',
      name: 'Cool Mist',
      description: 'Soft blue-gray for focused work',
      preview: '🌊',
      colors: ['#f8fafb', '#eef2f5', '#dde4e9']
    },
    {
      id: 'sage',
      name: 'Sage Garden',
      description: 'Calming green tones for peaceful vibes',
      preview: '🌿',
      colors: ['#f7faf7', '#eef5ee', '#dde9dd']
    },
    {
      id: 'lavender',
      name: 'Lavender Dream',
      description: 'Gentle purple hues for creativity',
      preview: '💜',
      colors: ['#faf8fb', '#f3f0f5', '#e9e4ed']
    },
    {
      id: 'system',
      name: 'System',
      description: 'Match your device settings',
      preview: '🔄',
      colors: ['auto', 'auto', 'auto']
    }
  ];

  const handleThemeChange = (themeId: string) => {
    setCurrentTheme(themeId as any);
    themeManager.setTheme(themeId as any);
  };

  // Listen for external theme changes
  useEffect(() => {
    const handleThemeUpdate = (theme: string) => {
      setCurrentTheme(theme as any);
    };

    themeManager.addListener(handleThemeUpdate);

    return () => {
      themeManager.removeListener(handleThemeUpdate);
    };
  }, []);

  return (
    <div className="tab-content theme-picker-content">
      <h3>🎨 Choose Your Vibe</h3>
      <p className="theme-description">
        Pick a theme that matches your mood. Each one has its own personality!
      </p>

      <div className="theme-grid">
        {themes.map(theme => (
          <button
            key={theme.id}
            className={`theme-option ${currentTheme === theme.id ? 'active' : ''}`}
            onClick={() => handleThemeChange(theme.id)}
          >
            <div className="theme-preview">
              <span className="theme-emoji">{theme.preview}</span>
              <div className="theme-color-dots">
                {theme.colors.map((color, index) => (
                  <span
                    key={index}
                    className="color-dot"
                    style={{
                      background: color === 'auto' ?
                        'linear-gradient(45deg, #fff 50%, #000 50%)' :
                        color
                    }}
                  />
                ))}
              </div>
            </div>
            <h4 className="theme-name">{theme.name}</h4>
            <p className="theme-desc">{theme.description}</p>
            {currentTheme === theme.id && (
              <span className="theme-active-badge">Active</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// OpenAI voice as returned by GET /api/voices
interface OpenAIVoice {
  id: string;
  name: string;
  description: string;
  personality?: string;
  bestFor?: string;
  ttsCompatible: boolean;
}

/**
 * Get the API base URL - same pattern as AuthContext
 */
function getApiBaseUrl(): string {
  // In production, use same origin (empty string)
  if (process.env.NODE_ENV === 'production') {
    return '';
  }
  // In development, use the backend port
  return process.env.REACT_APP_API_URL || 'http://localhost:3001';
}

// localStorage keys shared with InputArea for read-aloud preferences
const TTS_VOICE_KEY = 'emcog_tts_voice';
const TEXT_TO_VOICE_KEY = 'emcog_text_to_voice';
const DEFAULT_TTS_VOICE = 'ash';

// Preview emoji per OpenAI voice id (falls back to 🎵)
const VOICE_PREVIEW_EMOJI: { [id: string]: string } = {
  alloy: '🎧',
  ash: '🎭',
  ballad: '🎼',
  coral: '🪸',
  echo: '🌊',
  sage: '🌿',
  shimmer: '✨',
  verse: '🎶'
};

// Voice Picker Tab Component - Fetches OpenAI voices from /api/voices.
// Only ttsCompatible voices are offered here (read-aloud). Realtime voice
// mode can use all ten, but that choice is the EmCog's own agency.
const VoicePickerTab: React.FC = () => {
  const [currentVoice, setCurrentVoice] = useState<string>(
    () => localStorage.getItem(TTS_VOICE_KEY) || DEFAULT_TTS_VOICE
  );
  const [textToVoice, setTextToVoice] = useState<boolean>(
    () => localStorage.getItem(TEXT_TO_VOICE_KEY) === 'true'
  );
  const [availableVoices, setAvailableVoices] = useState<OpenAIVoice[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch the voice list from the server, keeping only read-aloud
  // compatible voices.
  const fetchVoices = async (): Promise<OpenAIVoice[]> => {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}/api/voices`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch voices: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    if (!data.voices || !Array.isArray(data.voices)) {
      throw new Error('Voice API returned invalid data');
    }

    return (data.voices as OpenAIVoice[]).filter(voice => voice.ttsCompatible);
  };

  // Load voices from API on mount
  useEffect(() => {
    let cancelled = false;

    const loadVoices = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const voices = await fetchVoices();
        if (!cancelled) {
          setAvailableVoices(voices);
        }
      } catch (err) {
        console.error('Voice loading error:', err);
        if (!cancelled) {
          setError('Failed to load voices');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadVoices();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleVoiceChange = (voiceId: string) => {
    setCurrentVoice(voiceId);
    localStorage.setItem(TTS_VOICE_KEY, voiceId);
    // Tell InputArea (and anyone else) the read-aloud voice changed
    window.dispatchEvent(new CustomEvent<string>('emcog-tts-voice-changed', {
      detail: voiceId
    }));
  };

  const handleTextToVoiceToggle = (enabled: boolean) => {
    setTextToVoice(enabled);
    localStorage.setItem(TEXT_TO_VOICE_KEY, String(enabled));
    window.dispatchEvent(new CustomEvent<boolean>('emcog-text-to-voice-changed', {
      detail: enabled
    }));
  };

  const handleRefreshVoices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const voices = await fetchVoices();
      setAvailableVoices(voices);
    } catch (err) {
      console.error('Voice refresh error:', err);
      setError('Failed to refresh voices');
    } finally {
      setIsLoading(false);
    }
  };

  // Stay in sync with changes made elsewhere (InputArea's 🔊 toggle)
  useEffect(() => {
    const handleVoiceChanged = (event: Event) => {
      const voiceId = (event as CustomEvent<string>).detail;
      if (voiceId) {
        setCurrentVoice(voiceId);
      }
    };

    const handleTextToVoiceChanged = (event: Event) => {
      setTextToVoice((event as CustomEvent<boolean>).detail);
    };

    window.addEventListener('emcog-tts-voice-changed', handleVoiceChanged);
    window.addEventListener('emcog-text-to-voice-changed', handleTextToVoiceChanged);

    return () => {
      window.removeEventListener('emcog-tts-voice-changed', handleVoiceChanged);
      window.removeEventListener('emcog-text-to-voice-changed', handleTextToVoiceChanged);
    };
  }, []);

  const currentVoiceDetails = availableVoices.find(v => v.id === currentVoice);

  return (
    <div className="tab-content voice-picker-content">
      <div className="voice-header">
        <h3>🎙️ Voice Selection</h3>
        <button
          className="refresh-voices-button"
          onClick={handleRefreshVoices}
          disabled={isLoading}
          title="Refresh voice list"
        >
          {isLoading ? '⏳' : '🔄'}
        </button>
      </div>
      <p className="voice-description">
        Choose the voice EmCog uses to read responses aloud. Voices are OpenAI TTS voices.
      </p>

      {error && (
        <div className="voice-error-message">
          ⚠️ {error}
        </div>
      )}

      {/* Text-to-Voice Toggle */}
      <div className="voice-setting-section">
        <div className="setting-toggle-row">
          <div className="setting-info">
            <span className="setting-label">Text-to-Voice Mode</span>
            <span className="setting-hint">Type messages, get voice responses</span>
          </div>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={textToVoice}
              onChange={(e) => handleTextToVoiceToggle(e.target.checked)}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {/* Current Voice Display */}
      {currentVoiceDetails && (
        <div className="current-voice-display">
          <span className="current-voice-label">Currently Active:</span>
          <div className="current-voice-info">
            <span className="current-voice-emoji">{VOICE_PREVIEW_EMOJI[currentVoiceDetails.id] || '🎵'}</span>
            <span className="current-voice-name">{currentVoiceDetails.name}</span>
          </div>
        </div>
      )}

      {/* Voice Grid */}
      {isLoading ? (
        <div className="voice-loading">
          <span className="loading-spinner">⏳</span>
          <p>Loading voices...</p>
        </div>
      ) : (
        <div className="voice-grid">
          {availableVoices.map(voice => (
            <button
              key={voice.id}
              className={`voice-option ${currentVoice === voice.id ? 'active' : ''}`}
              onClick={() => handleVoiceChange(voice.id)}
            >
              <div className="voice-preview">
                <span className="voice-emoji">{VOICE_PREVIEW_EMOJI[voice.id] || '🎵'}</span>
              </div>
              <h4 className="voice-name">{voice.name}</h4>
              <p className="voice-desc">{voice.description || voice.personality || 'No description'}</p>
              {currentVoice === voice.id && (
                <span className="voice-active-badge">Active</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Voice Tips */}
      <div className="voice-tips">
        <h4>How to Use</h4>
        <ul>
          <li><strong>Live Voice:</strong> Use the 🎙️ Voice button in the input area for a realtime conversation</li>
          <li><strong>Text-to-Voice:</strong> Toggle above to hear responses spoken</li>
          <li>The voice picked here is for read-aloud responses; in live voice mode the EmCog chooses its own voice</li>
          <li>Click 🔄 to refresh the voice list</li>
        </ul>
      </div>
    </div>
  );
};

export default Sidebar;